import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Theme } from '../types';
import { getNavLinks } from '../config/nav';
import ScoreTicker from './ScoreTicker';
import BrandLockup from './BrandLockup';
import { useHeaderScroll } from '../hooks/useHeaderScroll';
import type { UseLiveSportsReturn } from '../hooks/useLiveSports';
import { appConfig } from '../config';
import { useOptionalCart } from '../context/CartContext';

interface SiteHeaderProps {
  theme: Theme;
  onToggleTheme: () => void;
  liveSports?: UseLiveSportsReturn;
}

const SunIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const MoonIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

const SiteHeader: React.FC<SiteHeaderProps> = ({ theme, onToggleTheme, liveSports }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { atTop, hidden } = useHeaderScroll();
  const isNight = theme === 'dark';
  const navLinks = getNavLinks();
  const cart = useOptionalCart();

  const isActive = (path: string) =>
    location.pathname === path || (path === '/blog' && location.pathname.startsWith('/blog/'));

  // Collapse the sheet whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const closeOnScroll = () => setMenuOpen(false);
    window.addEventListener('scroll', closeOnScroll, { passive: true });
    return () => window.removeEventListener('scroll', closeOnScroll);
  }, [menuOpen]);

  return (
    <>
      {/* The wire — live scores, scrolls away; only in view at the very top */}
      <ScoreTicker liveSports={liveSports} />

      <nav
        className="mhd"
        aria-label="Primary"
        data-attop={atTop}
        data-hidden={hidden && !menuOpen}
      >
        <div className="mhd__inner">
          <Link to="/" className="mhd__brand" aria-label="2 Girls 1 Parlay home">
            <BrandLockup size={36} wordSize={18} />
          </Link>

          <div className="mhd__nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                aria-current={isActive(link.path) ? 'page' : undefined}
                className="mhd__link"
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="mhd__actions">
            {appConfig.features.enableShop && (
              <Link
                to="/cart"
                className="mhd__cart"
                aria-label={`Cart, ${cart?.itemCount ?? 0} item${cart?.itemCount === 1 ? '' : 's'}`}
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 4h2l1.6 10.2a2 2 0 0 0 2 1.7h7.9a2 2 0 0 0 1.9-1.4L21 7H6M9 20h.01M17 20h.01" />
                </svg>
                <span className="mhd__cart-label">Cart</span>
                {(cart?.itemCount ?? 0) > 0 && <span className="mhd__cart-count" key={cart?.itemCount}>{cart?.itemCount}</span>}
              </Link>
            )}
            <button
              type="button"
              onClick={onToggleTheme}
              className="mhd__icon"
              aria-label={`Switch to ${isNight ? 'light' : 'dark'} mode`}
            >
              {isNight ? <SunIcon /> : <MoonIcon />}
            </button>

            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className="mhd__icon mhd__menu-btn"
              aria-label="Toggle navigation menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        <div
          id="mobile-nav"
          className="mhd__sheet"
          data-open={menuOpen}
          hidden={!menuOpen}
        >
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              aria-current={isActive(link.path) ? 'page' : undefined}
              className="mhd__sheet-link"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </nav>
    </>
  );
};

export default SiteHeader;
